import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  ChevronLeft, Phone, MapPin, Mail, Pencil, MoreHorizontal,
  PhoneCall, UserCheck, FileText, FileSignature, CheckCircle2, Building2, Activity, Camera, CalendarDays,
} from 'lucide-react';
import { RecapDialog } from '../components/recap/RecapDialog';
import { RecapHistory } from '../components/recap/RecapHistory';

const ROLE_LABEL = {
  admin: 'Administrateur',
  employee: 'Commercial',
  employer: 'Commercial',
  investisseur: 'Investisseur',
  associe: 'Investisseur',
};

const TYPE_META = {
  appel: { icon: PhoneCall, color: 'bg-green-100 text-green-700' },
  client_transforme: { icon: UserCheck, color: 'bg-blue-100 text-blue-700' },
  devis_envoye: { icon: FileText, color: 'bg-purple-100 text-purple-700' },
  contrat_signe: { icon: FileSignature, color: 'bg-orange-100 text-orange-700' },
  tache_terminee: { icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-700' },
  prospect_ajoute: { icon: Building2, color: 'bg-amber-100 text-amber-700' },
  autre: { icon: Activity, color: 'bg-gray-100 text-gray-700' },
};

const fmtDateTime = (ts) => {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return ts;
  }
};

const initials = (name = '') =>
  name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');

export default function MonProfil({ user, onLogout }) {
  const navigate = useNavigate();
  const params = useParams();
  // URL param userEmail OR fallback to logged-in user's email (for /mon-profil)
  const targetEmail = params.userEmail ? decodeURIComponent(params.userEmail) : user?.email;

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activityType, setActivityType] = useState('all');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', phone: '', location: '', avatar_url: '' });
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showRecap, setShowRecap] = useState(false);
  const [recapDate, setRecapDate] = useState(null);
  const historyRef = useRef(null);
  const [settingsForm, setSettingsForm] = useState({ monthly_objective: 150000, bonus_rate: 5 });

  const isAdmin = user?.role === 'admin';
  const isSelf = (user?.email || '').toLowerCase() === (targetEmail || '').toLowerCase();

  useEffect(() => {
    if (!targetEmail) return;
    const load = async () => {
      try {
        setLoading(true);
        const params = activityType !== 'all' ? `?activity_type=${activityType}` : '';
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/profiles/${encodeURIComponent(targetEmail)}${params}`);
        setProfile(res.data);
        setEditForm({
          name: res.data?.user?.name || '',
          phone: res.data?.user?.phone || '',
          location: res.data?.user?.location || '',
          avatar_url: res.data?.user?.avatar_url || '',
        });
        setSettingsForm({
          monthly_objective: res.data?.performance?.monthly_objective || 150000,
          bonus_rate: Math.round((res.data?.performance?.bonus_rate || 0.05) * 100),
        });
      } catch (e) {
        console.error(e);
        toast.error('Profil introuvable ou accès refusé');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [targetEmail, activityType]);

  const saveProfile = async () => {
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/profiles/${encodeURIComponent(targetEmail)}`, editForm);
      toast.success('Profil mis à jour');
      setShowEditModal(false);
      setProfile((p) => ({ ...p, user: { ...p.user, ...editForm } }));
      // Si l'utilisateur modifie son propre profil, recharger pour propager l'avatar partout
      if (isSelf) {
        setTimeout(() => window.location.reload(), 500);
      }
    } catch (e) {
      toast.error("Échec de la mise à jour");
    }
  };

  const saveSettings = async () => {
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/profiles/${encodeURIComponent(targetEmail)}/settings`, {
        monthly_objective: Number(settingsForm.monthly_objective),
        bonus_rate: Number(settingsForm.bonus_rate) / 100,
      });
      toast.success('Objectif et prime mis à jour');
      setShowSettingsModal(false);
      // refresh
      const params = activityType !== 'all' ? `?activity_type=${activityType}` : '';
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/profiles/${encodeURIComponent(targetEmail)}${params}`);
      setProfile(res.data);
    } catch (e) {
      toast.error("Échec de la mise à jour");
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="mon-profil">
        <div className="text-center py-20 text-gray-500">Chargement du profil...</div>
      </Layout>
    );
  }
  if (!profile) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="mon-profil">
        <div className="text-center py-20 text-gray-500">Profil introuvable.</div>
      </Layout>
    );
  }

  const u = profile.user;
  const stats = profile.stats;
  const perf = profile.performance;
  const activities = profile.activities || [];
  const entreprises = profile.entreprises_signees || [];

  return (
    <Layout user={user} onLogout={onLogout} currentPage="mon-profil">
      <div className="space-y-6" data-testid="profil-page">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500">
          {isAdmin && (
            <>
              <button onClick={() => navigate('/utilisateurs')} className="hover:text-blue-600 flex items-center gap-1">
                <ChevronLeft className="h-4 w-4" /> Utilisateurs
              </button>
              <span>›</span>
            </>
          )}
          <span className="text-gray-900 font-semibold">Profil de {u.name}</span>
        </div>

        {/* Top grid: left identity card + right performance */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Identity & stats */}
          <Card className="lg:col-span-2">
            <CardContent className="p-6">
              <div className="flex items-start gap-5">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold shadow-md overflow-hidden">
                    {u.avatar_url ? (
                      <img src={u.avatar_url} alt="avatar" className="w-full h-full object-cover" />
                    ) : (
                      initials(u.name || u.email)
                    )}
                  </div>
                  {u.is_online && (
                    <span className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 border-2 border-white rounded-full" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-2xl font-bold text-gray-900 truncate">{u.name || u.email}</h1>
                  </div>
                  <p className="text-blue-600 font-semibold">{ROLE_LABEL[u.role] || u.role}</p>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 text-sm text-gray-600">
                    <span className="flex items-center gap-2"><Mail className="h-4 w-4" />{u.email}</span>
                    {u.phone && <span className="flex items-center gap-2"><Phone className="h-4 w-4" />{u.phone}</span>}
                    {u.location && <span className="flex items-center gap-2"><MapPin className="h-4 w-4" />{u.location}</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <span className={`inline-flex items-center gap-1.5 text-xs ${u.is_online ? 'text-green-700' : 'text-gray-500'}`}>
                      <span className={`w-2 h-2 rounded-full ${u.is_online ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                      {u.is_online ? 'En ligne' : 'Hors ligne'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {(isAdmin || isSelf) && (
                    <Button variant="outline" size="sm" onClick={() => setShowEditModal(true)} data-testid="edit-profile-btn">
                      <Pencil className="h-4 w-4 mr-1" /> Modifier le profil
                    </Button>
                  )}
                  {(isAdmin || isSelf) && (
                    <Button size="sm" onClick={() => { setRecapDate(null); setShowRecap(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="recap-open-btn">
                      <CalendarDays className="h-4 w-4 mr-1" /> Récapitulatif du jour
                    </Button>
                  )}
                </div>
              </div>

              {/* 6 KPI inline */}
              <div className="grid grid-cols-2 sm:grid-cols-6 gap-4 mt-6 pt-6 border-t border-gray-100">
                {[
                  { label: 'Prospects attribués', value: stats.prospects_assigned || 0, color: 'text-violet-600' },
                  { label: 'Clients transformés', value: stats.clients_transformes, color: 'text-gray-900' },
                  { label: 'Contrats signés', value: stats.contrats_signes, color: 'text-gray-900' },
                  { label: 'Devis envoyés', value: stats.devis_envoyes, color: 'text-gray-900' },
                  { label: 'Tâches effectuées', value: stats.taches_effectuees, color: 'text-gray-900' },
                  { label: 'Taux de conversion', value: `${stats.taux_conversion}%`, color: 'text-green-600' },
                ].map((k) => (
                  <div key={k.label} className="text-center">
                    <p className="text-xs text-gray-500">{k.label}</p>
                    <p className={`text-2xl font-bold mt-1 ${k.color}`}>{k.value}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Performance & primes */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-gray-900">Performance & Primes</h2>
                {isAdmin && (
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowSettingsModal(true)} data-testid="settings-objective-btn">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-gray-100 p-3">
                  <p className="text-[11px] uppercase text-gray-500 font-semibold">Chiffre d'affaires généré</p>
                  <p className="text-2xl font-bold text-emerald-600 mt-1">{(perf.ca_month || 0).toLocaleString('fr-FR')} €</p>
                  <p className="text-[11px] text-gray-500 mt-1">Ce mois</p>
                </div>
                <div className="rounded-xl border border-gray-100 p-3">
                  <p className="text-[11px] uppercase text-gray-500 font-semibold">Prime estimée</p>
                  <p className="text-2xl font-bold text-amber-600 mt-1">{(perf.prime_estimee || 0).toLocaleString('fr-FR')} €</p>
                  <p className="text-[11px] text-gray-500 mt-1">Ce mois ({Math.round((perf.bonus_rate || 0) * 100)}%)</p>
                </div>
              </div>

              <div className="mt-5">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Objectif mensuel</span>
                  <span className="font-bold text-blue-600">{perf.objective_pct || 0}%</span>
                </div>
                <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all"
                    style={{ width: `${Math.min(100, perf.objective_pct || 0)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {(perf.ca_month || 0).toLocaleString('fr-FR')} € / {(perf.monthly_objective || 0).toLocaleString('fr-FR')} €
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Activity & Companies */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Activity timeline */}
          <Card className="lg:col-span-2">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <h2 className="font-bold text-gray-900">Historique des activités</h2>
                <div className="flex items-center gap-2">
                  <Select value={activityType} onValueChange={setActivityType}>
                    <SelectTrigger className="w-44" data-testid="activity-filter">
                      <SelectValue placeholder="Tous les types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les types</SelectItem>
                      <SelectItem value="appel">Appels</SelectItem>
                      <SelectItem value="client_transforme">Clients transformés</SelectItem>
                      <SelectItem value="devis_envoye">Devis envoyés</SelectItem>
                      <SelectItem value="contrat_signe">Contrats signés</SelectItem>
                      <SelectItem value="prospect_ajoute">Prospects ajoutés</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {activities.length === 0 ? (
                <p className="text-center py-12 text-gray-500 text-sm">Aucune activité enregistrée sur cette période.</p>
              ) : (
                <ul className="space-y-4">
                  {activities.slice(0, 30).map((a) => {
                    const meta = TYPE_META[a.type] || TYPE_META.autre;
                    const Icon = meta.icon;
                    return (
                      <li key={a.id} className="flex items-start gap-3 group">
                        <div className={`w-9 h-9 rounded-full ${meta.color} flex items-center justify-center flex-shrink-0`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900 text-sm">{a.label}</p>
                              {a.entreprise && (
                                <p className="text-xs text-gray-600 truncate">
                                  Entreprise : <span className="text-blue-600 font-medium">{a.entreprise}</span>
                                </p>
                              )}
                            </div>
                            <span className="text-[11px] text-gray-500 whitespace-nowrap text-right">
                              {fmtDateTime(a.timestamp)}
                            </span>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Companies signed */}
          <div className="space-y-6">
          {(isAdmin || isSelf) && (
            <RecapHistory ref={historyRef} userEmail={targetEmail} onOpen={(d) => { setRecapDate(d); setShowRecap(true); }} />
          )}
          <Card>
            <CardContent className="p-6">
              <h2 className="font-bold text-gray-900 mb-4">Entreprises obtenues / signées</h2>
              {entreprises.length === 0 ? (
                <p className="text-center py-8 text-gray-500 text-sm">Aucune entreprise signée pour le moment.</p>
              ) : (
                <div className="space-y-3">
                  {entreprises.slice(0, 8).map((e) => (
                    <div key={e.id} className="flex items-center justify-between border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                      <div className="min-w-0">
                        <p className="font-semibold text-sm text-gray-900 truncate">{e.entreprise}</p>
                        <p className="text-[11px] text-emerald-600">{e.type}</p>
                        <p className="text-[11px] text-gray-500">{fmtDateTime(e.date)}</p>
                      </div>
                      <span className="font-bold text-sm text-gray-900 whitespace-nowrap ml-2">
                        {(e.montant || 0).toLocaleString('fr-FR')} €
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-gray-500 mt-5 italic">
                Les montants affichés sont basés sur les contrats signés. Les primes sont calculées selon le barème en vigueur.
              </p>
            </CardContent>
          </Card>
          </div>
        </div>
      </div>

      {(isAdmin || isSelf) && (
        <RecapDialog open={showRecap} onOpenChange={setShowRecap} userEmail={targetEmail} canEdit={isSelf}
          initialDate={recapDate} onSaved={() => historyRef.current?.reload()}
          onShowHistory={() => { setShowRecap(false); setTimeout(() => document.getElementById('recap-history')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150); }} />
      )}

      {/* Edit profile modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier le profil</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Photo de profil */}
            <div className="flex items-center gap-4 pb-2 border-b">
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center overflow-hidden shadow-md">
                  {editForm.avatar_url ? (
                    <img src={editForm.avatar_url} alt="avatar" className="w-full h-full object-cover" data-testid="edit-avatar-preview" />
                  ) : (
                    <span className="text-white text-2xl font-bold">
                      {(editForm.name || user?.email || 'U').charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex-1 space-y-1.5">
                <Label className="text-sm">Photo de profil</Label>
                <div className="flex flex-wrap gap-2">
                  <label
                    htmlFor="edit-avatar-file"
                    className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors"
                    data-testid="edit-avatar-upload-label"
                  >
                    <Camera className="h-3.5 w-3.5" />
                    Choisir une photo…
                  </label>
                  <input
                    id="edit-avatar-file"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="edit-avatar-file"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 2 * 1024 * 1024) {
                        toast.error('Photo trop volumineuse (max 2 Mo)');
                        return;
                      }
                      const reader = new FileReader();
                      reader.onload = (ev) => {
                        setEditForm((f) => ({ ...f, avatar_url: ev.target?.result || '' }));
                      };
                      reader.readAsDataURL(file);
                    }}
                  />
                  {editForm.avatar_url && (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-50 text-red-600 hover:bg-red-100 text-xs font-medium border border-red-200"
                      onClick={() => setEditForm((f) => ({ ...f, avatar_url: '' }))}
                      data-testid="edit-avatar-remove"
                    >
                      Retirer
                    </button>
                  )}
                </div>
                <p className="text-[10px] text-gray-500">JPG, PNG · max 2 Mo</p>
              </div>
            </div>

            <div>
              <Label>Nom complet</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
            </div>
            <div>
              <Label>Localisation</Label>
              <Input value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} placeholder="ex: Bangui, RCA" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Annuler</Button>
            <Button onClick={saveProfile} className="bg-blue-600 hover:bg-blue-700" data-testid="save-profile-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin settings modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Objectif & taux de prime</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Objectif mensuel (€)</Label>
              <Input
                type="number"
                value={settingsForm.monthly_objective}
                onChange={(e) => setSettingsForm({ ...settingsForm, monthly_objective: e.target.value })}
              />
            </div>
            <div>
              <Label>Taux de prime (% du CA)</Label>
              <Input
                type="number"
                step="0.1"
                value={settingsForm.bonus_rate}
                onChange={(e) => setSettingsForm({ ...settingsForm, bonus_rate: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsModal(false)}>Annuler</Button>
            <Button onClick={saveSettings} className="bg-blue-600 hover:bg-blue-700" data-testid="save-settings-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
